
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Loader2, Check, X } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { hasAccess } from '@/utils/permissionService';

// Define the form schema
const hubspotFormSchema = z.object({
  enabled: z.boolean().default(false),
  apiKey: z.string().min(1, "API Key is required"),
  portalId: z.string().min(1, "Portal ID is required"),
  referralTag: z.string().default("New Referral"),
});

type HubspotFormValues = z.infer<typeof hubspotFormSchema>;

interface HubspotIntegrationSettingsProps {
  onSaved: () => Promise<void>;
}

const HubspotIntegrationSettings: React.FC<HubspotIntegrationSettingsProps> = ({ onSaved }) => {
  const { toast } = useToast();
  const [isTesting, setIsTesting] = useState(false);
  const [testStatus, setTestStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [isSaving, setIsSaving] = useState(false);

  // Initialize form with default values
  const form = useForm<HubspotFormValues>({
    resolver: zodResolver(hubspotFormSchema),
    defaultValues: {
      enabled: false,
      apiKey: '',
      portalId: '',
      referralTag: 'New Referral',
    },
  });

  // Check if user has access to this feature
  const canAccessFeature = hasAccess('admin') || hasAccess('superadmin');

  const testConnection = async (values: HubspotFormValues) => {
    if (!values.apiKey || !values.portalId) {
      toast({
        title: "Validation Error",
        description: "API Key and Portal ID are required to test the connection",
        variant: "destructive",
      });
      return;
    }

    setIsTesting(true);
    setTestStatus('idle');

    try {
      // Simulate API call to test HubSpot connection
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // In a real app, you would call an API to test the connection
      // const response = await testHubspotConnection(values);
      
      setTestStatus('success');
      toast({
        title: "Connection Successful",
        description: "Successfully connected to HubSpot API",
      });
    } catch (error) {
      setTestStatus('error');
      toast({
        title: "Connection Failed",
        description: "Failed to connect to HubSpot. Please check your credentials.",
        variant: "destructive",
      });
    } finally {
      setIsTesting(false);
    }
  };

  const onSubmit = async (values: HubspotFormValues) => {
    setIsSaving(true);
    try {
      // Simulate saving the configuration
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // In a real app, you would save the configuration to your backend
      // await saveHubspotConfig(values);
      
      await onSaved();
      toast({
        title: "Settings Saved",
        description: "HubSpot integration settings have been saved",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save HubSpot settings",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (!canAccessFeature) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Access Denied</CardTitle>
          <CardDescription>
            You don't have permission to access HubSpot integration settings.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <svg
            className="mr-2 h-5 w-5"
            viewBox="0 0 512 512"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M93.667 152.777V271.11a20 20 0 0040 0v-78.889h53.222a73.333 73.333 0 1173.333 73.334h-60a20 20 0 000 40h60a113.334 113.334 0 100-226.667H133.667a20 20 0 00-40 0v73.89z"
              fill="currentColor"
            />
            <path
              d="M437.333 352.777h-60a20 20 0 000 40h60a73.333 73.333 0 110-146.667H384a20 20 0 000 40h53.333a33.333 33.333 0 010 66.667z"
              fill="currentColor"
            />
            <path
              d="M230 352.777h-16.667a33.333 33.333 0 010-66.666H230a20 20 0 000-40h-16.667a73.333 73.333 0 000 146.666H230a20 20 0 000-40z"
              fill="currentColor"
            />
          </svg>
          HubSpot Integration
        </CardTitle>
        <CardDescription>
          Configure HubSpot integration to automatically create contacts from referrals
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="enabled"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Enable HubSpot Integration</FormLabel>
                    <FormDescription>
                      Automatically create contacts in HubSpot when new referrals are added
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField
                control={form.control}
                name="apiKey"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>HubSpot API Key</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter your HubSpot API Key"
                        {...field}
                        type="password"
                      />
                    </FormControl>
                    <FormDescription>
                      Your HubSpot private app API key
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="portalId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>HubSpot Portal ID</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter your HubSpot Portal ID"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Your HubSpot account portal ID
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="referralTag"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Referral Tag</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Tag for referral contacts"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    This tag will be added to contacts created from referrals
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex items-center justify-between pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => testConnection(form.getValues())}
                disabled={isTesting || isSaving}
              >
                {isTesting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Testing Connection
                  </>
                ) : testStatus === 'success' ? (
                  <>
                    <Check className="mr-2 h-4 w-4 text-green-500" />
                    Connection Successful
                  </>
                ) : testStatus === 'error' ? (
                  <>
                    <X className="mr-2 h-4 w-4 text-red-500" />
                    Connection Failed
                  </>
                ) : (
                  'Test Connection'
                )}
              </Button>

              <Button type="submit" disabled={isSaving || isTesting}>
                {isSaving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving
                  </>
                ) : (
                  'Save Settings'
                )}
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default HubspotIntegrationSettings;
